// RepartitionTableauSystematiqueRepechage.jsx
// -----------------------------------------------------------------------------
// Tableau à élimination directe AVEC repêchage systématique
// - Tableau principal
// - Repêchage systématique : tous les perdants avant les demi-finales sont repêchés
// - Les perdants des demi-finales entrent dans les combats pour la 3e place
// - Chaque perdant d'un repêchage est éliminé
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";

import CombatModal from "./../CombatModal";
import CombatTableauModal from "./../CombatTableauModal";
import EditCombatTableauModal from "./../EditCombatTableauModal";

import {
  getDistributionCombats,
  getCompetitionCombats,
} from "../../services/combatsServices";
import { finalizeDistribution } from "../../services/distributionsServices";
import { updateCompetiteurResults } from "../../services/competiteursServices";
import { generateRoundNames } from "../../utils/roundUtils";

import TableauSystematiqueRepechage from "./TableauSystematiqueRepechage";
import ClassementSystematiqueRepechageModal from "./ClassementSystematiqueRepechageModal";

import {
  computePodiumFromSystematicRepechage,
} from "./tableauRepechageSystematique/systematicRepechageUtils";

/* -------------------------------------------------------------------------- */
/* Seed utils                                                                  */
/* -------------------------------------------------------------------------- */

const nextPow2 = (n) => (n <= 1 ? 1 : 1 << Math.ceil(Math.log2(n)));

const parseIds = (ids) => {
  if (Array.isArray(ids)) return ids;

  if (typeof ids === "string") {
    try {
      const arr = JSON.parse(ids);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }

  return [];
};

const hashStr = (s) => {
  let h = 0;

  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }

  return h >>> 0;
};

const mulberry32 = (a) => {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const shuffleWithRng = (arr, rng) => {
  const a = arr.slice();

  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }

  return a;
};

const buildFirstRoundSeed = (orderedCompetiteurs, distributionId) => {
  const base = nextPow2(orderedCompetiteurs.length);
  const pairCount = base / 2;
  const missing = base - orderedCompetiteurs.length;

  if (missing <= 0) return orderedCompetiteurs.slice();

  const seedStr = `${distributionId}:${orderedCompetiteurs
    .map((c) => c.id)
    .join(",")}`;

  const rng = mulberry32(hashStr(seedStr));

  const allPairs = Array.from({ length: pairCount }, (_, i) => i);
  const byePairs = new Set(
    shuffleWithRng(allPairs, rng)
      .slice(0, missing)
      .sort((a, b) => a - b),
  );

  const byeSide = new Map();
  Array.from(byePairs).forEach((p) => {
    byeSide.set(p, rng() < 0.5 ? 0 : 1);
  });

  const out = [];
  let i = 0;

  for (let p = 0; p < pairCount; p++) {
    const remainingPairs = pairCount - p;
    const remainingCompetitors = orderedCompetiteurs.length - i;

    const mustForceBye = remainingCompetitors === remainingPairs;
    const isBye = mustForceBye || byePairs.has(p);

    if (isBye) {
      const side = byeSide.has(p) ? byeSide.get(p) : rng() < 0.5 ? 0 : 1;

      if (side === 0) {
        out.push(null);
        out.push(orderedCompetiteurs[i++]);
      } else {
        out.push(orderedCompetiteurs[i++]);
        out.push(null);
      }
    } else {
      out.push(orderedCompetiteurs[i++]);
      out.push(orderedCompetiteurs[i++]);
    }
  }

  return out;
};

const computeTotalsByCompetitorFromCombats = (combats = []) => {
  const map = new Map();

  const toNum = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const addOne = (id, myPts, oppPts, resultLetter) => {
    const cid = Number(id);
    if (!Number.isFinite(cid)) return;

    const prev = map.get(cid) || {
      fights: 0,
      wins: 0,
      points: 0,
    };

    prev.fights += 1;

    const my = toNum(myPts);
    prev.points += my;

    const result = String(resultLetter || "")
      .trim()
      .toUpperCase();

    if (result === "V") {
      prev.wins += 1;
    } else if (!result) {
      const opp = toNum(oppPts);
      if (my > opp) prev.wins += 1;
    }

    map.set(cid, prev);
  };

  combats.forEach((combat) => {
    addOne(
      combat.competiteur_id1,
      combat.points_competiteur1,
      combat.points_competiteur2,
      combat.resultat_competiteur1,
    );

    addOne(
      combat.competiteur_id2,
      combat.points_competiteur2,
      combat.points_competiteur1,
      combat.resultat_competiteur2,
    );
  });

  return map;
};

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

const RepartitionTableauSystematiqueRepechage = ({
  competiteurs,
  distributionId,
  competitionId,
  tapisId,
  distributionCompetiteurs,
  refreshDistribution = () => {},
  discipline,
}) => {
  const [combats, setCombats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFinalized, setIsFinalized] = useState(false);

  const [isClassementOpen, setIsClassementOpen] = useState(false);

  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCombat, setSelectedCombat] = useState(null);
  const [positions, setPositions] = useState({
    position1: null,
    position2: null,
  });

  const [isScoreboardModalOpen, setIsScoreboardModalOpen] = useState(false);
  const [selectedScoreboardCombat, setSelectedScoreboardCombat] =
    useState(null);
  const [scoreboardPositions, setScoreboardPositions] = useState({
    position1: null,
    position2: null,
  });

  const seedIds = useMemo(() => {
    const ids = parseIds(distributionCompetiteurs);
    return ids.length ? ids : competiteurs.map((c) => c.id);
  }, [distributionCompetiteurs, competiteurs]);

  const byId = useMemo(
    () => new Map(competiteurs.map((c) => [c.id, c])),
    [competiteurs],
  );

  const competiteursOrdered = useMemo(
    () => seedIds.map((id) => byId.get(id)).filter(Boolean),
    [seedIds, byId],
  );

  const firstRoundSeed = useMemo(
    () =>
      buildFirstRoundSeed(competiteursOrdered, String(distributionId ?? "")),
    [competiteursOrdered, distributionId],
  );

  const roundNames = useMemo(
    () => generateRoundNames(firstRoundSeed.length),
    [firstRoundSeed.length],
  );

  const fetchCombats = async () => {
    setLoading(true);

    try {
      const fetched = await getDistributionCombats(distributionId);
      setCombats(fetched || []);
    } catch (error) {
      console.error("Erreur fetchCombats", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCombats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributionId]);

  const openManualCreateModal = (c1, c2, p1, p2) => {
    setSelectedCombat({
      competiteur1: c1,
      competiteur2: c2,
    });

    setPositions({
      position1: p1,
      position2: p2,
    });

    setIsManualModalOpen(true);
  };

  const openEditModal = (combatData, c1, c2, p1, p2) => {
    setSelectedCombat({
      competiteur1: c1,
      competiteur2: c2,
      combatData,
    });

    setPositions({
      position1: p1,
      position2: p2,
    });

    setIsEditModalOpen(true);
  };

  const openScoreboardModal = (c1, c2, p1, p2) => {
    setSelectedScoreboardCombat({
      competiteur1: c1,
      competiteur2: c2,
    });

    setScoreboardPositions({
      position1: p1,
      position2: p2,
    });

    setIsScoreboardModalOpen(true);
  };

  const handleManualSubmit = async () => {
    setIsManualModalOpen(false);
    await fetchCombats();
  };

  const handleScoreboardSubmit = async () => {
    setIsScoreboardModalOpen(false);
    await fetchCombats();
  };

  const confirmAndFinalize = async () => {
    const ok = window.confirm(
      "⚠️ Finaliser la distribution avec repêchage systématique ?\nCette action est irréversible et figera le classement.",
    );

    if (!ok) return;

    await handleFinalizeDistribution();
  };

  const handleFinalizeDistribution = async () => {
    try {
      setLoading(true);

      const tableauCombats = await getDistributionCombats(distributionId);
      setCombats(tableauCombats || []);

      const podium = computePodiumFromSystematicRepechage({
        firstRoundSeed,
        competiteurs: competiteursOrdered,
        combats: tableauCombats || [],
        roundNames,
      });

      if (!podium) {
        alert(
          "Impossible de calculer le podium.\nVérifie que la finale et les deux combats pour les places de 3e sont saisis.",
        );
        return;
      }

      const { champion, runnerUp, bronzes } = podium;
      const bronzeSet = new Set(bronzes || []);

      const allCombats = await getCompetitionCombats(competitionId);
      const totalsById = computeTotalsByCompetitorFromCombats(allCombats || []);

      const response = await finalizeDistribution(distributionId);

      await Promise.all(
        competiteursOrdered.map((competiteur) => {
          let classement_position = 0;

          if (competiteur.id === champion) classement_position = 1;
          else if (competiteur.id === runnerUp) classement_position = 2;
          else if (bronzeSet.has(competiteur.id)) classement_position = 3;

          const totals = totalsById.get(Number(competiteur.id));

          const safeNum = (value) => {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
          };

          const total_victoires =
            totals?.wins != null
              ? safeNum(totals.wins)
              : safeNum(competiteur.total_victoires);

          const total_points =
            totals?.points != null
              ? safeNum(totals.points)
              : safeNum(competiteur.total_points);

          return updateCompetiteurResults(competiteur.id, {
            classement_position,
            total_victoires,
            total_points,
          });
        }),
      );

      if (response) {
        alert("Distribution finalisée avec succès !");
        setIsFinalized(true);

        try {
          refreshDistribution();
        } catch (_) {}
      }
    } catch (error) {
      console.error("Erreur lors de la finalisation :", error);
      alert("Une erreur est survenue pendant la finalisation.");
    } finally {
      setLoading(false);
    }
  };

  if (isFinalized) return null;

  if (loading) {
    return (
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 text-sm font-semibold text-slate-500 shadow-sm dark:border-white/10 dark:bg-slate-900 dark:text-slate-300">
        Chargement du tableau avec repêchage systématique...
      </div>
    );
  }

  return (
    <div className="mt-8 relative">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={fetchCombats}
          className="rounded-2xl bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700"
        >
          Recharger
        </button>

        <button
          type="button"
          onClick={() => setIsClassementOpen(true)}
          className="rounded-2xl bg-orange-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600"
        >
          Classement
        </button>

        <button
          type="button"
          onClick={confirmAndFinalize}
          className="rounded-2xl bg-red-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-red-600"
        >
          Finaliser la distribution
        </button>
      </div>

      <TableauSystematiqueRepechage
        competiteurs={competiteursOrdered}
        combats={combats}
        roundNames={roundNames}
        firstRoundSeed={firstRoundSeed}
        onOpenCreate={openManualCreateModal}
        onOpenEdit={openEditModal}
        onOpenScoreboard={openScoreboardModal}
      />

      {isScoreboardModalOpen && selectedScoreboardCombat && (
        <CombatModal
          isOpen={isScoreboardModalOpen}
          onClose={() => setIsScoreboardModalOpen(false)}
          competiteur1={selectedScoreboardCombat.competiteur1}
          competiteur2={selectedScoreboardCombat.competiteur2}
          onSubmit={handleScoreboardSubmit}
          initialData={selectedScoreboardCombat || {}}
          distributionId={distributionId}
          position1={scoreboardPositions.position1}
          position2={scoreboardPositions.position2}
          refreshPouleTable={fetchCombats}
          competitionId={competitionId}
          tapisId={tapisId}
          discipline={discipline}
        />
      )}

      {isManualModalOpen && selectedCombat && (
        <CombatTableauModal
          isOpen={isManualModalOpen}
          onClose={() => setIsManualModalOpen(false)}
          competiteur1={selectedCombat.competiteur1}
          competiteur2={selectedCombat.competiteur2}
          onSubmit={handleManualSubmit}
          initialData={null}
          distributionId={distributionId}
          competitionId={competitionId}
          position1={positions.position1}
          position2={positions.position2}
          discipline={discipline}
        />
      )}

      {isEditModalOpen && selectedCombat && (
        <EditCombatTableauModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          competiteur1={selectedCombat.competiteur1}
          competiteur2={selectedCombat.competiteur2}
          initialData={selectedCombat.combatData}
          distributionId={distributionId}
          competitionId={competitionId}
          position1={positions.position1}
          position2={positions.position2}
          refreshTableau={fetchCombats}
          discipline={discipline}
        />
      )}

      <ClassementSystematiqueRepechageModal
        isOpen={isClassementOpen}
        onClose={() => setIsClassementOpen(false)}
        firstRoundSeed={firstRoundSeed}
        competiteurs={competiteursOrdered}
        combats={combats}
        roundNames={roundNames}
      />
    </div>
  );
};

export default RepartitionTableauSystematiqueRepechage;